import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { RadioGroup } from '../ui/RadioGroup';
import { CheckboxGroup } from '../ui/CheckboxGroup';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Button } from '../ui/Button';
import { MascotPanel } from '../ui/MascotPanel';
import { LabelWithInfo } from '../ui/LabelWithInfo';
import { PlusIcon, XIcon } from 'lucide-react';
import { yesNoUnknownOptions, currentCmsOptions, accessKnownOptions, integrationOptions } from '../../data/intakeOptions';
import { toOptions } from '../../utils/options';
const reveal = {
  initial: {
    opacity: 0,
    height: 0
  },
  animate: {
    opacity: 1,
    height: 'auto'
  },
  exit: {
    opacity: 0,
    height: 0
  },
  transition: {
    duration: 0.25,
    ease: [0.23, 1, 0.32, 1]
  }
};
export function Step12Tech() {
  const {
    data,
    updateData
  } = useIntake();
  const [newDomain, setNewDomain] = useState('');
  const hasCurrentSite = data.existingWebsiteUrl.trim() !== '';
  const addDesiredDomain = () => {
    if (newDomain.trim()) {
      updateData({
        desiredDomains: [...data.desiredDomains, newDomain.trim()]
      });
      setNewDomain('');
    }
  };
  const removeDesiredDomain = (index: number) => {
    updateData({
      desiredDomains: data.desiredDomains.filter((_, i) => i !== index)
    });
  };
  const handleIntegrations = (vals: string[]) => {
    const added = vals.find(v => !data.integrations.includes(v));
    if (added === 'none') {
      updateData({
        integrations: ['none'],
        integrationsOther: ''
      });
    } else {
      updateData({
        integrations: vals.filter(v => v !== 'none')
      });
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-3xl mx-auto space-y-8" data-id="element-64" data-mp="1uwe1w4id7mzq">
      <MascotPanel message="Bijna klaar! Nog even wat praktische zaken rondom domeinnamen, hosting en koppelingen." data-id="element-65" data-mp="jhr38jb516yl" />

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-8" data-id="element-66" data-mp="l0inze2kje6z">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-67" data-mp="pyslhvbrmqvr">Techniek & Hosting</h2>

        <div data-id="element-68" data-mp="bz17vvp5vcia">
          <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-69" data-mp="bzkg2kp0emj4">
            Heeft u al een domeinnaam geregistreerd? (bijv. www.uwbedrijf.nl)
          </label>
          <RadioGroup columns={2} value={data.domainOwned} onChange={val => updateData({
          domainOwned: val
        })} options={[{
          value: 'yes',
          label: 'Ja, ik heb al een domeinnaam'
        }, {
          value: 'no',
          label: 'Nee, deze moet nog geregistreerd worden'
        }]} data-id="element-70" data-mp="28y5x602ao7t" />
        </div>

        <AnimatePresence data-id="element-71" data-mp="3tfit2emzxbzk">
          {data.domainOwned === 'yes' && <motion.div key="domain-yes" {...reveal} className="space-y-6 pt-4 border-t border-gray-100 overflow-hidden" data-id="element-72" data-mp="3fgj75eqvbb73">
              <Input label="Welk domein is dit?" placeholder="bijv. www.mijnbedrijf.nl" value={data.domainName} onChange={e => updateData({
            domainName: e.target.value
          })} data-id="element-73" data-mp="30s9e9qm1fl4f" />

              <Input label="Waar is deze gehost?" placeholder="bijv. TransIP, Hostnet..." value={data.domainHost} onChange={e => updateData({
            domainHost: e.target.value
          })} data-id="element-74" data-mp="3aovws8qw603a" />

              <div data-id="element-75" data-mp="3etbz601aqpdr">
                <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-76" data-mp="3es0ywa727zre">
                  Beheert u dit zelf of via een 3e partij?
                </label>
                <RadioGroup columns={2} value={data.domainManagement} onChange={val => updateData({
              domainManagement: val
            })} options={[{
              value: 'self',
              label: 'Ik beheer dit zelf'
            }, {
              value: 'third_party',
              label: 'Via een 3e partij'
            }]} data-id="element-77" data-mp="3ohu0cidhmyhx" />
              </div>

              {data.domainManagement === 'third_party' && <Input label="Welke 3e partij?" placeholder="Naam van de partij of contactpersoon" value={data.domainThirdParty} onChange={e => updateData({
            domainThirdParty: e.target.value
          })} data-id="element-78" data-mp="25jpacxmryo9o" />}

              <div data-id="element-79" data-mp="1hv61afvwi7gj">
                <LabelWithInfo label="Draait uw e-mail (bijv. info@uwbedrijf.nl) op dit domein?" tooltip="Belangrijk: bij het verhuizen van het domein zorgen wij dat uw e-mail blijft werken." data-id="element-80" data-mp="1humtvt3vn1gr" />
                <RadioGroup columns={3} value={data.emailsLinked} onChange={val => updateData({
              emailsLinked: val
            })} options={toOptions(yesNoUnknownOptions)} data-id="element-81" data-mp="2y8ms8zn2qj1w" />
              </div>

              <div data-id="element-82" data-mp="qopbykelsvba">
                <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-83" data-mp="mkzpq1leiptf">
                  Vindt u het erg als uw website tijdelijk offline is tijdens de
                  bouw?
                </label>
                <p className="text-sm text-gray-500 mb-3" data-id="element-84" data-mp="cvpzkra4bh7y">
                  Tijdens de ontwikkeling kan het nodig zijn dat uw huidige
                  website tijdelijk niet bereikbaar is. We proberen dit zo kort
                  mogelijk te houden.
                </p>
                <RadioGroup columns={2} value={data.websiteOfflineAcceptable} onChange={val => updateData({
              websiteOfflineAcceptable: val
            })} options={[{
              value: 'yes',
              label: 'Ja, dat is geen probleem'
            }, {
              value: 'no',
              label: 'Nee, liever niet',
              description: 'We bouwen dan op een staging omgeving'
            }]} data-id="element-85" data-mp="2zd5vtekp3d7" />
              </div>
            </motion.div>}

          {data.domainOwned === 'no' && <motion.div key="domain-no" {...reveal} className="space-y-4 pt-4 border-t border-gray-100 overflow-hidden" data-id="element-86" data-mp="1yg4rzyhzi2hq">
              <label className="block text-sm font-semibold text-gray-900" data-id="element-87" data-mp="2r4acql91frhk">
                Welke domeinnaam wilt u registreren?
              </label>
              <p className="text-sm text-gray-500" data-id="element-88" data-mp="2h4rwaesc6nu9">
                U kunt meerdere domeinnamen opgeven als u twijfelt of meerdere
                wilt reserveren.
              </p>

              {data.desiredDomains.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-89" data-mp="15ol2jwusns7a">
                  {data.desiredDomains.map((domain, index) => <div key={index} className="flex items-center bg-[#F0F7FF] border border-[#007AFF]/20 rounded-full px-3 py-1.5" data-id="element-90" data-mp="1aqagbk9eijz2" data-mpi={index}>
                      <span className="text-sm font-medium text-[#007AFF]" data-id="element-91" data-mp="y5ljxtf5q4oz">
                        {domain}
                      </span>
                      <button type="button" aria-label={`${domain} verwijderen`} onClick={() => removeDesiredDomain(index)} className="ml-2 p-0.5 text-[#007AFF] hover:text-red-500 transition-colors" data-id="element-92" data-mp="176s3kbd3rvmg">
                        <XIcon className="w-3.5 h-3.5" data-id="element-93" data-mp="t7k5jlsoetju" />
                      </button>
                    </div>)}
                </div>}

              <div className="flex gap-3" data-id="element-94" data-mp="oeh64x17wpvq">
                <div className="flex-1" data-id="element-95" data-mp="po88v11c7yh">
                  <Input placeholder="bijv. www.mijnbedrijf.nl" value={newDomain} onChange={e => setNewDomain(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDesiredDomain();
                }
              }} data-id="element-96" data-mp="omtogkt93c0" />
                </div>
                <Button variant="outline" onClick={addDesiredDomain} className="flex-shrink-0" aria-label="Domeinnaam toevoegen" data-id="element-97" data-mp="3lb8dlo0f6ph4">
                  <PlusIcon className="w-5 h-5" data-id="element-98" data-mp="3uabxuvbbpax7" />
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>

        {hasCurrentSite && <div className="space-y-6 pt-8 border-t border-gray-100" data-id="element-99" data-mp="7v0nvnsr3y0g">
            <div data-id="element-100" data-mp="cvexvbdwxi14">
              <h3 className="text-xl font-bold text-gray-900" data-id="element-101" data-mp="cvy620drgs1y">
                Uw huidige website
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-102" data-mp="hv1cnp9e36c1">
                {data.existingWebsiteUrl}
              </p>
            </div>

            <div data-id="element-103" data-mp="22a7j1w3cssbg">
              <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-104" data-mp="1xd7ddmaf1rkv">
                In welk systeem is uw huidige website gebouwd?
              </label>
              <RadioGroup columns={3} value={data.currentCms} onChange={val => updateData({
            currentCms: val
          })} options={toOptions(currentCmsOptions)} data-id="element-105" data-mp="1secu6j8je108" />
            </div>

            <div data-id="element-106" data-mp="2g35lg2e7rkoi">
              <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-107" data-mp="2g4qetooskxks">
                Heeft u de inloggegevens van de huidige website en het domein?
              </label>
              <RadioGroup columns={3} value={data.hostingAccessKnown} onChange={val => updateData({
            hostingAccessKnown: val
          })} options={toOptions(accessKnownOptions)} data-id="element-108" data-mp="26ef7ds5wg695" />
            </div>

            <Textarea label="Welke bestaande pagina's moeten zeker blijven of doorverwijzen?" infoTooltip="Zo behouden we uw huidige posities in Google." placeholder="Bijv. /diensten/belastingadvies, /contact, of de blogartikelen die veel bezoekers trekken..." value={data.migratePages} onChange={e => updateData({
          migratePages: e.target.value
        })} data-id="element-109" data-mp="2z0qc9f0jgtbr" />
          </div>}

        <div className="space-y-6 pt-8 border-t border-gray-100" data-id="element-110" data-mp="3h4bes5c11h2q">
          <h3 className="text-xl font-bold text-gray-900" data-id="element-111" data-mp="3d4kkhrnakitg">
            Koppelingen & metingen
          </h3>

          <div data-id="element-112" data-mp="3qvpcq8oby8tr">
            <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-113" data-mp="3qu4ikk5xgrwf">
              Welke koppelingen zijn nodig?
            </label>
            <CheckboxGroup columns={2} values={data.integrations} onChange={handleIntegrations} options={toOptions(integrationOptions)} data-id="element-114" data-mp="3lyp7u137vc48" />
            <AnimatePresence data-id="element-115" data-mp="jay5acgzow88">
              {data.integrations.includes('other') && <motion.div key="integrations-other" {...reveal} className="overflow-hidden" data-id="element-116" data-mp="j93nh710lk8d">
                  <div className="pt-4" data-id="element-117" data-mp="na71dnzi566y">
                    <Input placeholder="Welke andere koppeling(en)? Bijv. Exact Online, Salesforce..." value={data.integrationsOther} onChange={e => updateData({
                  integrationsOther: e.target.value
                })} data-id="element-118" data-mp="9djp8dyza9gg" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div data-id="element-119" data-mp="3mpk5g3v44070">
            <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-120" data-mp="3rqrcu1tcexd5">
              Heeft u al Google Analytics, Search Console of advertentie-pixels
              (Meta, LinkedIn)?
            </label>
            <RadioGroup columns={3} value={data.existingAnalytics} onChange={val => updateData({
            existingAnalytics: val
          })} options={toOptions(yesNoUnknownOptions)} data-id="element-121" data-mp="3hsagsqfjrsk8" />
          </div>
        </div>

        <div className="pt-8 border-t border-gray-100" data-id="element-122" data-mp="2tv5ioxpkmw15">
          <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-123" data-mp="2oxcay08mdtyr">
            Hoe wilt u de hosting en het onderhoud regelen?
          </label>
          <RadioGroup columns={1} value={data.hostingActive} onChange={val => updateData({
          hostingActive: val
        })} options={[{
          value: 'noord',
          label: 'Volledig ontzorgd via Noord Digital (Aanbevolen)',
          description: 'Inclusief updates, back-ups, beveiliging en support'
        }, {
          value: 'external',
          label: 'Ik regel zelf externe hosting',
          description: 'Let op: dit vereist extra configuratiewerk'
        }]} data-id="element-124" data-mp="2jx7wefr9v9b6" />

          <AnimatePresence data-id="element-125" data-mp="6tdy3ui1zzp8">
            {data.hostingActive === 'external' && <motion.div key="hosting-external" {...reveal} className="overflow-hidden" data-id="element-126" data-mp="bqwbbx33t131">
                <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl" data-id="element-127" data-mp="gqsdrrw5kkgh">
                  <p className="text-sm text-amber-800" data-id="element-128" data-mp="lgz1lpiuc0xo">
                    <strong data-id="element-129" data-mp="lijulapibbtf">Let op:</strong> Als u de hosting zelf regelt,
                    rekenen wij <strong data-id="element-130" data-mp="qfsf3hy4nrxu">10% extra kosten</strong> op het
                    totaalbedrag in verband met extra afstemming en technische
                    complexiteit.
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}